/**
 * Tub backsplash detection and region computation helpers.
 *
 * Finds bathtubs on a wall (via parentWallIds or geometric edge alignment)
 * and computes a tiling region sized to cover the bathtub's footprint width
 * on that wall, extending 1 foot above the top of the tub surface.
 */

import { belongsToWall } from './parentWallHelpers';
import { isObjectType } from './objectTypeUtils';
import { tubBandHeight } from './bathroomBandHeights';
import { wallFaceSpanY } from '@shared-lib/geometry/wallFace';
import {
  buildShowerFootprint,
  buildFixtureFootprint,
  calculateShowerWallTiling,
} from '@shared-lib/geometry/showerWallTiling';

// Tubs use a larger wall-detection tolerance than showers (~18 inches vs ~3 inches)
// because freestanding tubs with faucets may have a significant gap to the wall.
const TUB_WALL_TOLERANCE = 0.3;

const CORNER_MATCH_TOLERANCE = 0.15;

function getWallEndpoints2D(wall) {
  if (wall.bottomCorners && wall.bottomCorners.length >= 2) {
    return {
      endpoint1: { x: wall.bottomCorners[0].x, z: wall.bottomCorners[0].z },
      endpoint2: { x: wall.bottomCorners[1].x, z: wall.bottomCorners[1].z },
    };
  }
  const wallWidth = wall.dimensions.x || wall.dimensions.width;
  const rotY = wall.rotation?.y || 0;
  const tangent = { x: Math.cos(rotY), z: -Math.sin(rotY) };
  const half = wallWidth / 2;
  return {
    endpoint1: {
      x: wall.center.x + tangent.x * half,
      z: wall.center.z + tangent.z * half,
    },
    endpoint2: {
      x: wall.center.x - tangent.x * half,
      z: wall.center.z - tangent.z * half,
    },
  };
}

function wallsShareCornerAt(wallA, wallB) {
  const epA = getWallEndpoints2D(wallA);
  const epB = getWallEndpoints2D(wallB);

  const dist = (p1, p2) => {
    const dx = p1.x - p2.x;
    const dz = p1.z - p2.z;
    return Math.sqrt(dx * dx + dz * dz);
  };

  if (
    dist(epA.endpoint1, epB.endpoint1) < CORNER_MATCH_TOLERANCE ||
    dist(epA.endpoint1, epB.endpoint2) < CORNER_MATCH_TOLERANCE
  ) {
    return 'endpoint1';
  }

  if (
    dist(epA.endpoint2, epB.endpoint1) < CORNER_MATCH_TOLERANCE ||
    dist(epA.endpoint2, epB.endpoint2) < CORNER_MATCH_TOLERANCE
  ) {
    return 'endpoint2';
  }

  return null;
}

/**
 * Find a bathtub object on a given wall.
 *
 * First checks parentWallIds (direct assignment). If no match, uses geometric
 * alignment of the tub-surface footprint to detect walls the bathtub touches
 * (e.g. short side walls that aren't in parentWallIds).
 *
 * @param {string} wallId - Wall ID to check
 * @param {Object} room   - Room data with .objects and .walls arrays
 * @returns {Object|null}  The bathtub object, or null if none found
 */
export function findBathtubOnWall(wallId, room) {
  if (!wallId || !room?.objects) return null;

  // Pass 1: check parentWallIds (fast path)
  for (const obj of room.objects) {
    if (isObjectType(obj, 'bathtub') && belongsToWall(obj, wallId)) {
      return obj;
    }
  }

  // Pass 2: geometric check — does any bathtub's tub-surface footprint
  // have an edge aligned with this wall?
  for (const obj of room.objects) {
    if (!isObjectType(obj, 'bathtub')) continue;

    const footprint = buildTubFootprint(obj, room);
    const tilingResult = calculateShowerWallTiling(
      footprint,
      room,
      2.4,
      TUB_WALL_TOLERANCE
    );

    if (tilingResult.sides.some((side) => side.wallId === wallId)) {
      return obj;
    }
  }

  return null;
}

/**
 * Find ALL wall IDs that a bathtub touches (for multi-wall tiling).
 *
 * Uses the tub-surface dimensions (larger footprint) if available,
 * then the shared-lib geometry to detect which walls each edge aligns with.
 *
 * @param {Object} bathtub - Bathtub object with center, dimensions, rotation
 * @param {Object} room    - Room data with .walls and .objects arrays
 * @returns {string[]}      Array of wallIds the bathtub touches
 */
export function findAllTubWallIds(bathtub, room) {
  if (!bathtub || !room?.walls) return [];

  const footprint = buildTubFootprint(bathtub, room);
  console.log(
    '[findAllTubWallIds] footprint:',
    footprint.length,
    'points, center:',
    bathtub.center
  );
  const tilingResult = calculateShowerWallTiling(
    footprint,
    room,
    2.4,
    TUB_WALL_TOLERANCE
  );
  console.log(
    '[findAllTubWallIds] tilingResult.sides:',
    tilingResult.sides.map((s) => ({
      edgeIndex: s.edgeIndex,
      wallId: s.wallId,
      length: s.length,
    }))
  );

  return tilingResult.sides
    .filter((side) => side.wallId)
    .map((side) => side.wallId);
}

/**
 * Find the tub-surface child object for a given bathtub.
 * The tub-surface has parents: [{ id: tubId, type: 'bathtub' }].
 * Its dimensions are larger than the bathtub body and represent
 * the actual surface footprint against the wall.
 *
 * @param {Object} bathtub - The bathtub object
 * @param {Object} room    - Room data with .objects array
 * @returns {Object|null}  The tub-surface object, or null
 */
function findTubSurface(bathtub, room) {
  if (!bathtub?.id || !room?.objects) return null;

  return (
    room.objects.find(
      (obj) =>
        isObjectType(obj, 'tub-surface') &&
        obj.parents?.some((p) => p.id === bathtub.id && p.type === 'bathtub')
    ) || null
  );
}

function buildTubFootprint(bathtub, room) {
  if (
    bathtub.usePolygonSurround === true &&
    bathtub['tub-surround-polygon']?.points?.length >= 3
  ) {
    return buildFixtureFootprint(bathtub);
  }
  const tubSurface = findTubSurface(bathtub, room);
  return buildShowerFootprint(tubSurface || bathtub);
}

/**
 * Find the primary (back) wall for a bathtub and derive rotation info.
 * The primary wall is the one with the longest edge alignment.
 *
 * @param {Object} bathtub - Bathtub object with center, dimensions, rotation
 * @param {Object} room    - Room data with .walls and .objects arrays
 * @returns {{ primaryWallId: string|null, wallAngle: number|null, allWallIds: string[] }}
 */
export function findPrimaryTubWall(bathtub, room) {
  if (!bathtub || !room?.walls)
    return { primaryWallId: null, wallAngle: null, allWallIds: [] };

  const footprint = buildTubFootprint(bathtub, room);
  const tilingResult = calculateShowerWallTiling(
    footprint,
    room,
    2.4,
    TUB_WALL_TOLERANCE
  );

  const matchedSides = tilingResult.sides.filter((side) => side.wallId);
  const allWallIds = matchedSides.map((side) => side.wallId);

  if (matchedSides.length === 0) {
    return { primaryWallId: null, wallAngle: null, allWallIds: [] };
  }

  // Primary wall = longest edge alignment (the back wall of the tub)
  const primary = matchedSides.reduce((best, side) =>
    side.length > best.length ? side : best
  );

  return {
    primaryWallId: primary.wallId,
    wallAngle: primary.wallAngle,
    allWallIds,
  };
}

/**
 * The world Y of a tub's surface — what its backsplash height is measured
 * up from, whether its band starts there or at the floor (a freestanding or
 * corner tub). The tub-surface object when the tub has one, else the tub.
 */
export function tubSurfaceTopY(bathtub, room) {
  const src = findTubSurface(bathtub, room) || bathtub;
  return (src?.center?.y ?? 0) + (src?.dimensions?.y || 0) / 2;
}

/**
 * Compute the tub backsplash tiling region (percentage coordinates) for a
 * bathtub's tub-surface footprint projected onto a specific wall.
 *
 * Uses the tub-surface dimensions (larger than the bathtub body) for the
 * horizontal footprint projection.
 * Width = the tub-surface edge length along that wall.
 * Height = the wall's tub setting above the tub surface (1 foot with none).
 *
 * @param {Object} bathtub - Bathtub object (used to find tub-surface child)
 * @param {string} wallId  - Wall ID to compute the region for
 * @param {Object} room    - Room data with .walls and .objects arrays
 * @returns {Object}         { x, y, width, height } in percentages 0-100
 */
export function computeTubBacksplashRegion(
  bathtub,
  wallId,
  room,
  allTubWallIds = null
) {
  if (!bathtub || !wallId || !room?.walls) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  const wall = room.walls.find((w) => w.id === wallId);
  if (!wall) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }

  // Percentages of the DRAWN face (`wallFace`), which the 3D tiles over.
  const face = wallFaceSpanY(wall);
  if (!face) {
    return { x: 0, y: 0, width: 100, height: 100 };
  }
  const wallHeight = face.height;

  // Use tub-surface for vertical positioning (center.y, dimensions.y),
  // but use polygon footprint for horizontal edge detection on corner tubs
  const tubSurface = findTubSurface(bathtub, room);
  const footprintSource = tubSurface || bathtub;

  // --- Horizontal positioning (project tub edge onto wall) ---

  const footprint = buildTubFootprint(bathtub, room);
  const tilingResult = calculateShowerWallTiling(
    footprint,
    room,
    2.4,
    TUB_WALL_TOLERANCE
  );
  const tilingSide = tilingResult.sides.find((side) => side.wallId === wallId);

  // Wall endpoints — prefer bottomCorners (definitive 3D coords)
  // NOTE: bottomCorners[0] = UV 100% (right), bottomCorners[1] = UV 0% (left)
  const { endpoint1, endpoint2 } = getWallEndpoints2D(wall);

  const wallVec = {
    x: endpoint2.x - endpoint1.x,
    z: endpoint2.z - endpoint1.z,
  };
  const wallLenSq = wallVec.x * wallVec.x + wallVec.z * wallVec.z;

  let regionXPercent, regionWidthPercent;

  if (tilingSide && tilingSide.startPoint && tilingSide.endPoint) {
    // Project the matched edge endpoints onto the wall line
    const project = (pt) => {
      const v = { x: pt.x - endpoint1.x, z: pt.z - endpoint1.z };
      return ((v.x * wallVec.x + v.z * wallVec.z) / wallLenSq) * 100;
    };
    const pStart = project(tilingSide.startPoint);
    const pEnd = project(tilingSide.endPoint);

    let minP = Math.max(0, Math.min(pStart, pEnd));
    let maxP = Math.min(100, Math.max(pStart, pEnd));

    // Snap to wall edge if tub is close to the corner (within ~6 inches)
    // to avoid ugly gaps where backsplash doesn't quite reach the wall corner
    const wallLen = Math.sqrt(wallLenSq);
    const snapThreshold =
      wallLen > 0 ? (TUB_WALL_TOLERANCE / wallLen) * 100 : 5;
    if (minP < snapThreshold) minP = 0;
    if (maxP > 100 - snapThreshold) maxP = 100;

    // Flip for bottomCorners UV convention
    regionXPercent = 100 - maxP;
    regionWidthPercent = maxP - minP;
  } else {
    // Fallback: project footprintSource center onto wall and pick the tub
    // dimension that runs parallel to this wall (not always the longest one).
    const src = footprintSource;
    const vC = {
      x: src.center.x - endpoint1.x,
      z: src.center.z - endpoint1.z,
    };
    const tC = ((vC.x * wallVec.x + vC.z * wallVec.z) / wallLenSq) * 100;

    const wallWidth = wall.dimensions.x || wall.dimensions.width;
    const wallLen = Math.sqrt(wallLenSq);
    // Project both tub half-dimensions onto the wall direction to find
    // which one is more parallel to this wall.
    const rotY = src.rotation?.y || 0;
    const cosR = Math.cos(rotY);
    const sinR = Math.sin(rotY);
    const wallDir = {
      x: wallVec.x / (wallLen || 1),
      z: wallVec.z / (wallLen || 1),
    };
    const projX = Math.abs(cosR * wallDir.x + -sinR * wallDir.z);
    const projZ = Math.abs(sinR * wallDir.x + cosR * wallDir.z);
    const edgeLen =
      projX > projZ
        ? src.dimensions.x || src.dimensions.width || 0
        : src.dimensions.z || src.dimensions.depth || 0;
    regionWidthPercent = (edgeLen / wallWidth) * 100;

    // Flip for bottomCorners UV convention
    regionXPercent = 100 - tC - regionWidthPercent / 2;
  }

  // Corner-aware extension: if the tub also touches an adjacent wall that
  // shares a corner with this wall, extend the backsplash to that corner.
  // This applies to both the tilingSide and fallback paths above.
  if (allTubWallIds && allTubWallIds.length > 1) {
    for (const otherWallId of allTubWallIds) {
      if (otherWallId === wallId) continue;
      const otherWall = room.walls.find((w) => w.id === otherWallId);
      if (!otherWall) continue;

      const sharedAt = wallsShareCornerAt(wall, otherWall);
      if (sharedAt === 'endpoint1') {
        // endpoint1 = UV 100% (right edge). Extend region to right edge.
        const rightEdge = regionXPercent + regionWidthPercent;
        if (rightEdge < 100) {
          regionWidthPercent = 100 - regionXPercent;
        }
      } else if (sharedAt === 'endpoint2') {
        // endpoint2 = UV 0% (left edge). Extend region to left edge.
        if (regionXPercent > 0) {
          regionWidthPercent += regionXPercent;
          regionXPercent = 0;
        }
      }
    }
  }

  // --- Vertical positioning ---
  // Freestanding and corner tubs: from the floor to the band's top over the
  // tub. Otherwise (alcove, drop-in, undermount): from the tub's top. The
  // band's height is the wall's setting (`backsplashSpec.tub`) — 12" with
  // none, as it always was.
  const tubTopY = tubSurfaceTopY(bathtub, room);
  const wallBottomY = face.bottom;
  const BACKSPLASH_HEIGHT = tubBandHeight(wall, {
    tubTopY,
    wallTopY: wallBottomY + wallHeight,
  });
  const isFreestanding = bathtub.tubType === 'freestanding';
  const isCornerTub = allTubWallIds && allTubWallIds.length > 1;
  const extendToFloor = isFreestanding || isCornerTub;

  const startPercent = extendToFloor
    ? 0
    : ((tubTopY - wallBottomY) / wallHeight) * 100;
  const regionHeightPercent = extendToFloor
    ? ((tubTopY + BACKSPLASH_HEIGHT - wallBottomY) / wallHeight) * 100
    : (BACKSPLASH_HEIGHT / wallHeight) * 100;

  return {
    x: Math.max(0, Math.min(100, regionXPercent)),
    y: Math.max(0, Math.min(100, startPercent)),
    width: Math.min(100, regionWidthPercent),
    height: Math.max(0, Math.min(100, regionHeightPercent)),
  };
}
